import { Component, OnDestroy, ViewChild, ElementRef, AfterViewInit, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-avd-iframe',
    templateUrl: './avd-iframe.component.html',
    styleUrl: './avd-iframe.component.scss',
    standalone: false
})
export class AvdIframeComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('avdIframe', { static: false }) iframeRef!: ElementRef<HTMLIFrameElement>;
  
  readonly defaultAvdUrl = 'https://windows.cloud.microsoft/?navFrom=avdweb#/apps';
  readonly proxyUrl = `${environment.apiUrl}/avd/proxy`; // Endpoint de proxy no backend (se implementado)
  avdUrl: string;
  iframeSrc: string;
  showFallback = false; // Começa tentando carregar o iframe
  useProxy = false; // Tenta usar proxy se disponível
  private errorCheckTimeout: any;
  private originalConsoleError: any;
  private routeSubscription?: Subscription;

  constructor(private route: ActivatedRoute) {
    // Lê a URL dos query params, se fornecida
    const urlParam = this.route.snapshot.queryParams['url'];
    this.avdUrl = urlParam ? decodeURIComponent(urlParam) : this.defaultAvdUrl;
    
    console.log('🔍 AVD Component - URL recebida:', this.avdUrl);
    console.log('🔍 AVD Component - Query param original:', urlParam);
    
    // Tenta usar proxy primeiro, depois fallback para URL direta
    // O proxy precisa ser implementado no backend
    this.iframeSrc = this.avdUrl; // Usa URL fornecida ou padrão
    
    // Intercepta console.error para suprimir erros 904 do iframe
    this.originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      // Ignora erros relacionados ao iframe bloqueado (904)
      const errorString = JSON.stringify(args);
      if (errorString.includes('904') || 
          errorString.includes('ERR_BLOCKED_BY_CLIENT') ||
          errorString.includes('Refused to display') ||
          (args[0] && typeof args[0] === 'object' && Array.isArray(args[0]) && args[0][0]?.code === 904)) {
        // Não loga esse erro específico - é esperado quando o iframe é bloqueado
        // Mas marca o fallback como verdadeiro
        this.showFallback = true;
        return;
      }
      this.originalConsoleError.apply(console, args);
    };
    
    // Mostra o fallback após 1 segundo se o iframe não carregar
    // (Azure Virtual Desktop bloqueia iframes, então provavelmente será bloqueado)
    this.errorCheckTimeout = setTimeout(() => {
      if (!this.showFallback) {
        this.checkIframeStatus();
      }
    }, 1000);
  }

  ngOnInit(): void {
    // Observa mudanças nos query params para atualizar a URL
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      const urlParam = params['url'];
      console.log('🔍 AVD Component - Query params mudaram:', params);
      console.log('🔍 AVD Component - URL param:', urlParam);
      
      if (urlParam) {
        const newUrl = decodeURIComponent(urlParam);
        console.log('🔍 AVD Component - Nova URL decodificada:', newUrl);
        if (newUrl !== this.avdUrl) {
          console.log('🔄 AVD Component - Atualizando URL de', this.avdUrl, 'para', newUrl);
          this.avdUrl = newUrl;
          this.iframeSrc = this.avdUrl;
          this.showFallback = false;
          // Reseta o timeout para verificar o novo iframe
          if (this.errorCheckTimeout) {
            clearTimeout(this.errorCheckTimeout);
          }
          this.errorCheckTimeout = setTimeout(() => {
            if (!this.showFallback) {
              this.checkIframeStatus();
            }
          }, 1000);
        }
      } else {
        console.warn('⚠️ AVD Component - Nenhum query param "url" encontrado, usando URL padrão');
      }
    });
  }

  ngAfterViewInit(): void {
    // Adiciona handler de erro no iframe após ser renderizado
    if (this.iframeRef?.nativeElement) {
      this.iframeRef.nativeElement.addEventListener('error', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.showFallback = true;
      });
    }
  }

  ngOnDestroy(): void {
    if (this.errorCheckTimeout) {
      clearTimeout(this.errorCheckTimeout);
    }
    // Cancela a subscription dos query params
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    // Restaura console.error original
    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
    }
  }

  onIframeLoad(): void {
    clearTimeout(this.errorCheckTimeout);
    // Se o iframe carregou, verifica se realmente tem conteúdo
    setTimeout(() => {
      this.checkIframeStatus();
    }, 500);
  }

  onIframeError(): void {
    clearTimeout(this.errorCheckTimeout);
    this.showFallback = true;
    // Suprime o erro 904 do console (é esperado quando o iframe é bloqueado)
    // Não precisamos logar esse erro como crítico
  }

  private checkIframeStatus(): void {
    try {
      const iframe = this.iframeRef?.nativeElement;
      if (!iframe) {
        this.showFallback = true;
        return;
      }

      // Tenta verificar se o iframe foi bloqueado
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc || iframeDoc.body === null) {
          // Iframe bloqueado ou vazio
          this.showFallback = true;
        } else {
          // Iframe carregou com sucesso
          this.showFallback = false;
        }
      } catch (e) {
        // Erro de CORS - pode ser bloqueio de segurança
        // Mas também pode ser normal para sites externos
        // Verifica se há conteúdo visível
        this.showFallback = true;
      }
    } catch (error) {
      this.showFallback = true;
    }
  }

  openInNewWindow(): void {
    console.log('🔗 AVD Component - Abrindo URL em nova janela:', this.avdUrl);
    window.open(this.avdUrl, '_blank', 'noopener,noreferrer');
  }
  
  get currentUrl(): string {
    return this.avdUrl;
  }
}
